// Money helpers for the finance section.
//
// RULE: every money AMOUNT is an integer number of MINOR units of its currency
// (cents for AUD/USD; 0 decimals for JPY; 3 for KWD — taken from Intl, so any
// ISO-4217 code works). Store and calculate in minor units; convert to major
// units (dollars) only to display (`formatMoney`) or when reading user input
// (`parseMoney`). Rates, percentages, unit/share prices, quantities and FX
// rates are NOT money amounts — keep those as plain decimals and round to
// minor units only when they produce an amount (`multiplyMinor`).
//
// No exchange rates live here: converting between currencies is a separate
// concern (an amount always carries — or defaults to — one currency).

const LOCALE = 'en-AU';
export const DEFAULT_CURRENCY = 'AUD';

/** Validate + upper-case a 3-letter ISO-4217 code. Throws RangeError if malformed. */
export function normaliseCurrency(code = DEFAULT_CURRENCY) {
  const c = String(code ?? '')
    .trim()
    .toUpperCase();
  if (!/^[A-Z]{3}$/.test(c)) throw new RangeError(`Invalid currency code: ${code}`);
  return c;
}

const digitsCache = new Map();

/** Number of minor-unit digits for a currency (AUD 2, JPY 0, KWD 3). */
export function minorUnitDigits(currency = DEFAULT_CURRENCY) {
  const c = normaliseCurrency(currency);
  if (!digitsCache.has(c)) {
    const opts = new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency: c,
    }).resolvedOptions();
    digitsCache.set(c, opts.maximumFractionDigits);
  }
  return digitsCache.get(c);
}

/**
 * Round a (possibly fractional) minor-unit value to an integer, half away from
 * zero. Float noise is removed first (`toPrecision(15)`), so e.g.
 * 1.005 × 100 = 100.49999999999999 rounds to 101 as a human expects.
 */
export function roundMinor(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) throw new RangeError(`Not a finite amount: ${value}`);
  const rounded = Math.round(Number(Math.abs(n).toPrecision(15)));
  return n < 0 && rounded !== 0 ? -rounded : rounded;
}

/** Major units (e.g. dollars, a JS number) → integer minor units. */
export function majorToMinor(major, currency = DEFAULT_CURRENCY) {
  return roundMinor(Number(major) * 10 ** minorUnitDigits(currency));
}

/** Integer minor units → major units (a JS number, for display/inputs only). */
export function minorToMajor(minor, currency = DEFAULT_CURRENCY) {
  return Number(minor) / 10 ** minorUnitDigits(currency);
}

/** An amount × a rate/factor (tax rate, FX, share of a split…), rounded to minor units. */
export function multiplyMinor(minor, factor) {
  return roundMinor(Number(minor) * Number(factor));
}

/** Sum integer minor-unit amounts (ignores null/undefined). */
export function sumMinor(amounts) {
  let total = 0;
  for (const a of amounts) {
    if (a === null || a === undefined) continue;
    total += Number(a);
  }
  return total;
}

/**
 * Split `total` minor units into integer parts that sum EXACTLY to `total`
 * (largest-remainder method), e.g. an annual salary into pay periods or a
 * repayment between payers.
 *   allocateMinor(1000, 3)          → [334, 333, 333]
 *   allocateMinor(1000, [2, 1, 1])  → [500, 250, 250]
 * `parts` is a count (equal shares) or an array of non-negative weights.
 * Ties for the leftover units go to the earliest parts.
 */
export function allocateMinor(total, parts) {
  const weights = Array.isArray(parts) ? parts.map(Number) : Array(Number(parts)).fill(1);
  if (weights.length === 0) throw new RangeError('allocateMinor: need at least one part');
  if (weights.some((w) => !Number.isFinite(w) || w < 0)) {
    throw new RangeError('allocateMinor: weights must be non-negative numbers');
  }
  const weightSum = weights.reduce((s, w) => s + w, 0);
  if (weightSum === 0) throw new RangeError('allocateMinor: weights sum to zero');

  const amount = roundMinor(total);
  const sign = amount < 0 ? -1 : 1;
  const abs = Math.abs(amount);
  const exact = weights.map((w) => (abs * w) / weightSum);
  const result = exact.map(Math.floor);
  let leftover = abs - result.reduce((s, v) => s + v, 0);
  const byRemainder = exact
    .map((v, i) => ({ i, rem: v - Math.floor(v) }))
    .sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (let k = 0; leftover > 0; k = (k + 1) % byRemainder.length, leftover -= 1) {
    result[byRemainder[k].i] += 1;
  }
  return result.map((v) => (sign < 0 && v !== 0 ? -v : v));
}

// ─── Money values ({ amount, currency }) ─────────────────────────────────────
//
// Every STORED money value carries its currency: `{ amount, currency }`, where
// `amount` is integer minor units for an amount of money, or a decimal in major
// units for a per-unit price (a share price). Derived figures (pay timelines,
// tax summaries) stay plain AUD minor units (`…Cents`).

/** Currencies a money value may be entered in (the ones with exchange rates). */
export const SUPPORTED_CURRENCIES = Object.freeze(['AUD', 'USD']);
/** For values locked to Australian dollars (salary, super, tax figures…). */
export const AUD_ONLY = Object.freeze(['AUD']);

/** A money value: `{ amount, currency }` (currency validated + upper-cased). */
export function money(amount, currency = DEFAULT_CURRENCY) {
  return { amount, currency: normaliseCurrency(currency) };
}

function blank(v) {
  return v === null || v === undefined || v === '';
}

/**
 * Read a stored money value `{ amount, currency }`, or null when absent /
 * invalid (anything but that shape, an unsupported currency, a bad amount).
 * @param {object} [opts]
 * @param {Array<string>} [opts.currencies] - allowed currencies (others → null).
 * @param {boolean} [opts.price] - a per-unit decimal price (else integer minor units).
 * @param {number|null} [opts.min] - smallest allowed amount (null = any).
 * @param {boolean} [opts.positive] - the amount must be > 0.
 */
export function readMoney(
  value,
  { currencies = SUPPORTED_CURRENCIES, price = false, min = null, positive = false } = {},
) {
  if (!value || typeof value !== 'object') return null;
  const raw = value.amount;
  if (blank(raw) || blank(value.currency)) return null;
  let c;
  try {
    c = normaliseCurrency(value.currency);
  } catch {
    return null;
  }
  if (!currencies.includes(c)) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  const amount = price ? n : roundMinor(n);
  if (positive && !(amount > 0)) return null;
  if (min !== null && amount < min) return null;
  return { amount, currency: c };
}

/**
 * A money value (minor units) converted to AUD minor units at `rate` (AU$ per
 * 1 unit of its currency — see market.js `audRateFor`). AUD needs no rate;
 * null when the value is missing or there's no rate.
 */
export function toAudCents(value, rate) {
  if (!value) return null;
  if (value.currency === 'AUD') return value.amount;
  if (!(rate > 0)) return null;
  return majorToMinor(minorToMajor(value.amount, value.currency) * rate, 'AUD');
}

/**
 * The amount of `count` units at a per-unit `price` value, in the price's
 * minor units: (10, { amount: 2.505, currency: 'USD' }) → 2505. Null without
 * a price.
 */
export function priceTotal(count, price) {
  if (!price || !Number.isFinite(price.amount)) return null;
  return majorToMinor(Number(count) * price.amount, price.currency);
}

/**
 * Parse user-typed money ("$1,234.56", "-12.3", "(45.00)", "USD 10") into
 * integer minor units of `currency`. Returns null for blank/invalid input.
 * Extra decimals are rounded half away from zero.
 */
export function parseMoney(text, currency = DEFAULT_CURRENCY) {
  if (text === null || text === undefined) return null;
  let s = String(text).trim();
  if (!s) return null;
  let negative = false;
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1);
  }
  // Drop currency codes/symbols, grouping commas and whitespace.
  s = s.replace(/[A-Za-z]{3}/g, '').replace(/[$€£¥,\s]/g, '');
  if (s.startsWith('-')) {
    negative = !negative;
    s = s.slice(1);
  } else if (s.endsWith('-')) {
    negative = !negative;
    s = s.slice(0, -1);
  }
  if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  const minor = majorToMinor(Number(s), currency);
  return negative && minor !== 0 ? -minor : minor;
}

const formatterCache = new Map();

function formatter(key, options) {
  if (!formatterCache.has(key)) formatterCache.set(key, new Intl.NumberFormat(LOCALE, options));
  return formatterCache.get(key);
}

// ─── Display ─────────────────────────────────────────────────────────────────
//
// RULES (every finance page follows these):
//  1. Money in TEXT (tiles, card details, labels, toasts) carries an
//     unambiguous symbol: "AU$1,234.56", "US$280.00", "£40.00", "JP¥500"
//     (`formatMoney`, `formatPrice`).
//  2. Money in a TABLE names the currency once, in the column header
//     ("Price (US$)" — `moneyHeader`), and the cells use the bare symbol
//     ("$40.23" — the `short: true` option).
//  3. An exchange rate reads "US$1 = AU$1.4123" (`formatRate`).

/**
 * Unambiguous symbols for currencies whose own symbol is shared ("$", "¥").
 * Any other currency uses its narrow symbol when that's a one-currency mark
 * (£, €, ₹…), else its ISO code ("CHF", "SEK").
 */
const TEXT_SYMBOLS = Object.freeze({
  AUD: 'AU$',
  USD: 'US$',
  NZD: 'NZ$',
  CAD: 'CA$',
  SGD: 'S$',
  HKD: 'HK$',
  MXN: 'MX$',
  JPY: 'JP¥',
  CNY: 'CN¥',
});
/** Narrow symbols (lower-cased) used by more than one currency — never shown alone in text. */
const AMBIGUOUS_SYMBOLS = new Set(['$', '¥', 'kr', '£', 'rs', 'r']);

const narrowCache = new Map();

/** The currency's narrow symbol ("$" for AUD/USD, "£", "€", "CHF"). */
function narrowSymbol(c) {
  if (!narrowCache.has(c)) {
    const parts = new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency: c,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    narrowCache.set(c, parts.find((p) => p.type === 'currency')?.value ?? c);
  }
  return narrowCache.get(c);
}

/**
 * The symbol for `currency`: unambiguous by default ("AU$", "US$", "£",
 * "CHF") — for text and table headers; `short: true` gives the bare symbol
 * ("$", "£") for table cells under a header that names the currency.
 * GBP's "£" is shared (e.g. EGP) but unambiguous enough on its own here.
 */
export function currencySymbol(currency = DEFAULT_CURRENCY, { short = false } = {}) {
  const c = normaliseCurrency(currency);
  const narrow = narrowSymbol(c);
  if (short) return narrow;
  if (TEXT_SYMBOLS[c]) return TEXT_SYMBOLS[c];
  if (c === 'GBP') return narrow;
  return AMBIGUOUS_SYMBOLS.has(narrow.toLowerCase()) ? c : narrow;
}

/** A table column header naming its currency: ("Price", 'USD') → "Price (US$)". */
export function moneyHeader(label, currency = DEFAULT_CURRENCY) {
  return `${label} (${currencySymbol(currency)})`;
}

/**
 * A right-aligned (numeric / money) table column, `{ label, numeric: true }`
 * (see `figuresTable`). Text columns stay plain strings and left-align.
 */
export function numericColumn(label) {
  return { label, numeric: true };
}

/**
 * Format a major-unit number with `currency`'s symbol (see `currencySymbol`;
 * a letter code gets a no-break space before the digits: "CHF 10.00").
 */
function formatWithSymbol(major, c, { digits, signDisplay, short }) {
  const fmt = formatter(`money|${c}|${digits}|${signDisplay}`, {
    style: 'currency',
    currency: c,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    signDisplay,
  });
  const symbol = currencySymbol(c, { short });
  const parts = fmt.formatToParts(major);
  return parts
    .map((p, i) => {
      if (p.type !== 'currency') return p.value;
      const next = parts[i + 1];
      const spaced = /\p{L}$/u.test(symbol) && next && next.type !== 'literal';
      return spaced ? `${symbol}\u00a0` : symbol;
    })
    .join('');
}

/**
 * Format integer minor units for display: "AU$1,234.56", "US$2.00" (rule 1);
 * `short: true` → "$1,234.56" for a table cell under a `moneyHeader` (rule 2).
 * `whole: true` rounds to whole major units ("AU$1,235") for summaries.
 * `signDisplay` passes through to Intl (e.g. 'exceptZero' for deltas).
 */
export function formatMoney(
  minor,
  { currency = DEFAULT_CURRENCY, whole = false, signDisplay = 'auto', short = false } = {},
) {
  const c = normaliseCurrency(currency);
  const digits = whole ? 0 : minorUnitDigits(c);
  return formatWithSymbol(minorToMajor(minor, c), c, { digits, signDisplay, short });
}

/**
 * Format a money value (`{ amount, currency }`, see `readMoney`) like
 * `formatMoney` — in its own currency. `price: true` for a per-unit price.
 */
export function formatMoneyValue(value, { price = false, ...opts } = {}) {
  if (price) return formatPrice(value.amount, value.currency, opts);
  return formatMoney(value.amount, { ...opts, currency: value.currency });
}

/**
 * A per-unit price (a decimal in major units, e.g. a share price) with its
 * currency. It may be stored more precisely than a cent (US$225.505) but is
 * DISPLAYED rounded to the currency's minor unit, half away from zero
 * ("US$225.51"); `short` as for `formatMoney`.
 */
export function formatPrice(price, currency = DEFAULT_CURRENCY, { short = false } = {}) {
  const c = normaliseCurrency(currency);
  return formatMoney(majorToMinor(price, c), { currency: c, short });
}

/**
 * An exchange rate as "US$1 = AU$1.4123": `rate` units of `quote` per ONE
 * unit of `base` (default AUD per USD), to `decimals` places.
 */
export function formatRate(rate, { base = 'USD', quote = 'AUD', decimals = 4 } = {}) {
  const fmt = formatter(`rate|${decimals}`, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${currencySymbol(base)}1 = ${currencySymbol(quote)}${fmt.format(Number(rate))}`;
}

/**
 * Minor units → an editable input value: grouped, no symbol, and decimals only
 * when there are cents ("90,000", "1,234.50"); '' for null/undefined.
 * Round-trips through `parseMoney`.
 */
export function formatAmountInput(minor, currency = DEFAULT_CURRENCY) {
  if (minor === null || minor === undefined || minor === '') return '';
  const c = normaliseCurrency(currency);
  const digits = Number(minor) % 10 ** minorUnitDigits(c) === 0 ? 0 : minorUnitDigits(c);
  const fmt = formatter(`input|${c}|${digits}`, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return fmt.format(minorToMajor(minor, c));
}

/** Fraction digits a per-unit price (e.g. a share price) may be ENTERED with. */
export const PRICE_DECIMALS = 4;

/**
 * Clean typed / pasted text down to the characters an amount can contain:
 * digits, grouping commas and ONE decimal point, with at most `decimals`
 * fraction digits (0 → no point at all); a leading "-" survives only with
 * `allowNegative` (when a "-" comes before the first digit).
 * "  $100.23    " → "100.23", "US$1,000" → "1,000", "12.345" (2) → "12.34".
 * Prefix-consistent (cleaning the text before the caret gives the caret's new
 * position), which the money input relies on.
 */
export function sanitiseAmountText(text, { decimals = 2, allowNegative = false } = {}) {
  const s = String(text ?? '');
  let out = '';
  let seenDigit = false;
  let point = false;
  let fraction = 0;
  let negative = false;
  for (const ch of s) {
    if (ch >= '0' && ch <= '9') {
      if (point && fraction >= decimals) continue;
      if (point) fraction += 1;
      seenDigit = true;
      out += ch;
    } else if (ch === '.') {
      if (point || decimals <= 0) {
        // A second point, or a point where no fraction is allowed: drop the
        // rest of the fraction too (nothing after it can be valid).
        if (!point) point = true;
        fraction = decimals;
        continue;
      }
      point = true;
      out += ch;
    } else if (ch === ',') {
      if (!point) out += ch;
    } else if (ch === '-' && allowNegative && !seenDigit && !point) {
      negative = true;
    }
  }
  return negative ? `-${out}` : out;
}

/**
 * A decimal (e.g. a per-share price) for an input: grouped, at least
 * `minDecimals` and up to `maxDecimals` fraction digits — 213.5 → "213.50",
 * 1234.5678 → "1,234.5678". Blank for null/undefined/NaN.
 */
export function formatDecimalInput(value, { minDecimals = 2, maxDecimals = 4 } = {}) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const fmt = formatter(`dec|${minDecimals}|${maxDecimals}`, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: Math.max(minDecimals, maxDecimals),
  });
  return fmt.format(n);
}

/** The currency's English name ("Australian Dollar"), for pickers; the code if unknown. */
export function currencyName(currency) {
  const c = normaliseCurrency(currency);
  try {
    return new Intl.DisplayNames(LOCALE, { type: 'currency' }).of(c) || c;
  } catch {
    return c;
  }
}

/** Format a fractional rate as a percentage: 0.12 → "12%", 0.12345 → "12.35%". */
export function formatPercent(rate, { decimals = 2 } = {}) {
  const fmt = formatter(`pct|${decimals}`, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
  return fmt.format(Number(rate));
}
